import { useCallback, useState } from 'react';

import {
  gateUrl,
  openScreen,
  reopenAnswerSchema,
  reopenNotice,
  screenErrorMessage,
  screenSessionSchema,
  screenTicketSchema,
  withForms,
  type OpenScreen,
  type ReopenAnswer,
  type ScreenTicket,
} from '../lib/mission/workshopScreen';
import { supabase } from '../lib/supabase';

type ScreenPhase = 'closed' | 'opening' | 'open';

/** A refusal already put in words for the card. */
class ScreenError extends Error {}

/** A one-minute ticket from the database: who may look or act is decided there, never here. */
async function issueTicket(threadId: string | null, controls: boolean): Promise<ScreenTicket> {
  // Not in the generated Supabase types yet (same escape hatch as useWorkshopControl).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.rpc as any)('issue_workshop_screen_ticket', {
    p_thread_id: threadId,
    p_controls: controls,
  });
  if (error) throw new ScreenError(screenErrorMessage({ message: error.message }));
  const parsed = screenTicketSchema.safeParse(data);
  if (!parsed.success) throw new ScreenError("Laissez-passer illisible : l'écran n'a pas été ouvert.");
  return parsed.data;
}

/** A POST to the VM's gate, the ticket as bearer: the user's session never leaves the cockpit. */
async function postToGate(ticket: ScreenTicket, path: string): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(gateUrl(ticket.screen_url, path), {
      method: 'POST',
      headers: { authorization: `Bearer ${ticket.ticket}` },
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
    });
  } catch {
    throw new ScreenError(screenErrorMessage({}));
  }
  if (!response.ok) throw new ScreenError(screenErrorMessage({ status: response.status }));
  return response;
}

/** Options of an opening. */
export interface OpenOptions {
  /**
   * A gesture on the screen (PLAN-ATELIER-OTTO § 17): opened to act, the browser is reopened to
   * every site at once if a workshop left it restricted — a login goes through Google, Microsoft…
   */
  reopenIfRestricted?: boolean;
}

/**
 * The workshop's screen, on demand: `open(controls)` asks the database for a ticket, the VM's gate
 * for a one-time connection, and hands it to the viewer. `reopenForms` asks root to reopen the forms
 * Chrome to every site (to log in to a new one); it needs the right to act, and says root's answer.
 */
export function useWorkshopScreen(threadId: string | null) {
  const [phase, setPhase] = useState<ScreenPhase>('closed');
  const [screen, setScreen] = useState<OpenScreen | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reopening, setReopening] = useState(false);

  const reopenForms = useCallback(async () => {
    setError(null);
    setNotice(null);
    setReopening(true);
    try {
      const response = await postToGate(await issueTicket(threadId, true), '/formulaires/ouvrir');
      const parsed = reopenAnswerSchema.safeParse(await response.json().catch(() => null));
      const answer: ReopenAnswer = parsed.success ? parsed.data : { requested: true };
      const forms = answer.forms;
      if (forms) setScreen((current) => (current ? withForms(current, forms) : current));
      const said = reopenNotice(answer);
      setNotice(said.notice);
      setError(said.error);
    } catch (cause) {
      setError(cause instanceof ScreenError ? cause.message : screenErrorMessage({}));
    } finally {
      setReopening(false);
    }
  }, [threadId]);

  const open = useCallback(async (controls: boolean, options: OpenOptions = {}) => {
    setPhase('opening');
    setError(null);
    setNotice(null);
    let opened: OpenScreen;
    try {
      const ticket = await issueTicket(threadId, controls);
      const session = screenSessionSchema.safeParse(await (await postToGate(ticket, '/session')).json());
      if (!session.success) throw new ScreenError("Réponse illisible de la machine : l'écran n'a pas été ouvert.");
      opened = openScreen(ticket, session.data);
      setScreen(opened);
      setPhase('open');
    } catch (cause) {
      setScreen(null);
      setPhase('closed');
      setError(cause instanceof ScreenError ? cause.message : screenErrorMessage({}));
      return;
    }
    if (options.reopenIfRestricted && !opened.viewOnly && opened.formsOpen === false) await reopenForms();
  }, [threadId, reopenForms]);

  const close = useCallback((reason: string | null = null) => {
    setScreen(null);
    setPhase('closed');
    setError(reason);
  }, []);

  return { phase, screen, error, notice, reopening, open, close, reopenForms };
}
